import * as errors from '@attain-apps/errors'
import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import * as models from '../../../../models'
import * as query from '../../../query'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CreateObject } from './create-object'
import { CreateTuple } from './create-tuple'

/**
 * Retrieves the ID of the desired ancestor(s) of the current record
 *
 * @param entitySelector {@link GetAncestorId.EntitySelector}
 * @example
 * // Retrieves the ID of the immediate parent of this record
 * { '@^': 'parent' }
 * // ===
 * '!floor_1'
 *
 * // Retrieves the ID of the root entity for the dimension of this record
 * { '@^': 'root' }
 * // ===
 * '?campus_1'
 *
 * // Retrieves the ID of the provided ancestor of this record
 * { '@^': '!bldg' }
 * // ===
 * '?bldg_1'
 *
 * // Retrieves the IDs of all ancestors of this record, as an object
 * { '@^': 'object' }
 * // ===
 * {
 *   '!campus': '?campus_1',
 *   '!bldg': '?bldg_1',
 *   '!floor': '!floor_1',
 * }
 *
 * // Retrieves the IDs of all ancestors of this record, as a list, with full entity IDs
 * { '@^': [ 'list', { entityIdFormat: 'full' } ] }
 * // ===
 * [
 *   { entityId: 'ent:!space:!campus', recordId: '?campus_1' },
 *   { entityId: 'ent:!space:!bldg', recordId: '?bldg_1' },
 *   { entityId: 'ent:!space:!floor', recordId: '!floor_1' },
 * ]
 */
export interface GetAncestorId<out T = any> {
  /** {@link GetAncestorId} */
  readonly [Key.GetAncestorId]:
    | GetAncestorId.EntitySelector<T>
    | [
        entitySelector: GetAncestorId.EntitySelector<T>,
        options: GetAncestorId.Options,
      ]
}
export namespace GetAncestorId {
  export const isGetAncestorId: TypeChecker<GetAncestorId> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<GetAncestorId>({
        [Key.GetAncestorId]: EntitySelector.isEntitySelector,
      }),
    ).usingAlias('Fn.GetAncestorId')
  export const assertIsGetAncestorId: TypeAsserter<GetAncestorId> =
    TypeAsserter.Factory.from(isGetAncestorId)

  /**
   * Determines which {@link Query.Target.Relation} are accepted here
   */
  const ANCESTOR_RELATION_CRITERIA = {
    inheritance: '+',
    singleton: true,
  } as const satisfies query.Relation.Where.Criteria

  /**
   * Determines which entity's information is being retrieved
   *
   * - `list` can be used to retrieve `{ entityId: Entity.Id, recordId: Record.Id }[]`
   * - `object` can be used to retrieve `Record<Entity.Id, Record.Id>`
   */
  export type EntitySelector<T = any> = T extends string
    ?
        | PartialId
        | models.Entity.Id
        | query.Relation.Where<typeof ANCESTOR_RELATION_CRITERIA>
    : T extends readonly unknown[]
      ? 'list'
      : T extends Object.Constraint
        ? 'object'
        : never
  export namespace EntitySelector {
    export const isEntitySelector: TypeChecker<EntitySelector> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.union(
          PartialId.isPartialId,
          models.Entity.Id.isId,
          query.Relation.Where.getTypeChecker(ANCESTOR_RELATION_CRITERIA),
          TypeChecker.Factory.oneOf('list', 'object'),
        ),
      ).usingAlias('GetAncestorId.EntitySelector')
  }

  export interface Options {
    /**
     * When using a {@link EntityIdSelector} that returns entity IDs, this
     * indicates whether the result should contain full entity IDs or partial
     * entity IDs
     *
     * @default Id.Format.Partial
     */
    entityIdFormat?: models.Id.Format
  }

  /**
   * @see {@link Node}
   */
  export class Node<
    T extends Object.Constraint | string = Object.Constraint | string,
  > extends fnNode.Node<
    T,
    Key.GetAncestorId,
    {
      /**
       * {@link PartialId} of the ancestor whose ID is desired
       */
      readonly entityPartialId: PartialId
    }
  > {
    public readonly type = common.value.types.Intrinsic.Text

    constructor(arg: Readonly<Node<T>['arg']>) {
      super(Key.GetAncestorId, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<GetAncestorId> = {
    createNode: (fn, context) => {
      const { modelId, schema } = context
      // We can only get ancestors if this expression is happening in the
      // context of an entity
      models.Entity.Id.assertIsId(modelId)
      // Extracts args
      const arg = fn[Key.GetAncestorId]
      let entitySelector: EntitySelector
      let options: Options
      if (TypeChecker.isReadonlyArray(arg)) {
        entitySelector = arg[0]
        options = arg[1]
      } else {
        entitySelector = arg
        options = {}
      }
      // If returning multiple IDs, we'll need a function to create a single
      // node from those IDs
      let _createNode: (
        elements: readonly {
          readonly entityId: PartialId | models.Entity.Id
          readonly recordId: expressionNode.Node
        }[],
      ) => expressionNode.Node
      if (entitySelector === 'list') {
        // Function to create a tuple (i.e. list)
        _createNode = elements =>
          new CreateTuple.Node(
            elements.map(
              ({ entityId, recordId }) =>
                new CreateObject.Node({
                  entityId: new expressionNode.ForValue(entityId),
                  recordId,
                }),
            ),
          )
      } else if (entitySelector === 'object') {
        // Function to create an object
        _createNode = elements =>
          new CreateObject.Node(
            elements.reduce(
              (prev, { entityId, recordId }) =>
                Object.assign(prev, {
                  [entityId]: recordId,
                }),
              {},
            ),
          )
      } else {
        // If returning a single ancestor's record ID, we need to resolve the
        // concrete entity ID of that ancestor
        const ancestorEntityId = query.Relation.isRelation(entitySelector)
          ? schema.tryGetRelative(modelId, entitySelector)?.id
          : models.Entity.Id.isId(entitySelector)
            ? entitySelector
            : models.Entity.Id.from({
                context: models.Entity.Id.Context.from(modelId),
                partialId: entitySelector,
              })
        if (!ancestorEntityId) {
          // If the requested relation does not exist for the current context's
          // entity ID (e.g. requesting 'parent' for a root entity), then we
          // return null
          return new expressionNode.ForValue(null)
        }
        if (!schema.isAncestor(ancestorEntityId, modelId)) {
          // If this is not actually an ancestor, we throw an error
          throw new errors.ValidationError({
            type: `ancestor ID for '${modelId}'`,
            value: ancestorEntityId,
          })
        }
        // If all checks below have passed, then we can return the node for this
        // ancestor ID
        return new Node({
          entityPartialId: models.Id.Parts.from(ancestorEntityId).partialId,
        })
      }
      // If we are returning multiple IDs, we need to get the entity IDs and
      // record ID expressions and then convert those into a single node using
      // the relevant function from above
      const ancestors = schema.getAncestorsOf(modelId)
      const { entityIdFormat = models.Id.Format.Partial } = options
      return _createNode(
        ancestors.map(ancestor => {
          const entityPartialId = models.Id.Parts.from(ancestor).partialId
          return {
            entityId:
              entityIdFormat === models.Id.Format.Full
                ? ancestor.id
                : entityPartialId,
            recordId: new Node({ entityPartialId }),
          }
        }),
      )
    },
    expressionsInArgs: fn => [],
    typeChecker: isGetAncestorId,
  }
}
